import {
  getAgentDisplayName,
  personalizeCopy,
  resolveAgentVisuals,
  resolveDefaultRoleLine,
  useNewConversation,
  usePersonalizeAgent,
} from '@tunasong/plugin-agent/integration'
import type { Agent, ColorName, Persisted } from '@tunasong/schemas'
import { Avatar, Button, getColorCSS, Link, Typography } from '@tunasong/ui-lib'
import type { CSSProperties, FC } from 'react'
import styles from './agent-identity-header.module.css'

export interface AgentIdentityHeaderProps {
  agent: Persisted<Agent>
  /** Composer id below — the roleLine renders `${composerId}-role-description` as its id. */
  composerId: string
  /** False when the tabs row already names the active agent (2+ agents). */
  showName: boolean
}

/**
 * Persistent identity header for the Agents tool (Joni spec §1). Renders on
 * EVERY thread state — empty or not — so the agent's identity and the
 * aria-describedby anchor never disappear. The empty-state starter chips live
 * separately in AgentChatEmptyHeader.
 *
 * Personalize agents (Joni's UX spec §1, §4): editors get a trailing
 * `Personalize` button, the one place a band names its agent, sets its voice
 * and writes how they work. When the band has written notes, a quiet caption
 * says the agent follows them, without quoting them.
 */
export const AgentIdentityHeader: FC<AgentIdentityHeaderProps> = props => {
  const { agent, composerId, showName } = props
  const { color, Icon } = resolveAgentVisuals(agent)
  const accent = getColorCSS(color as ColorName)
  const roleLine = resolveDefaultRoleLine(agent)
  const personalize = usePersonalizeAgent(agent)
  // "New conversation" (Chat threads per advisor): the same action as the palette Command
  const newConversation = useNewConversation(agent)
  const name = getAgentDisplayName(agent)
  const hasNotes = Boolean(agent.instructions?.trim())

  return (
    <div
      className={styles.container}
      style={
        {
          '--agent-accent': accent,
          '--agent-accent-header-bg': 'color-mix(in oklch, var(--agent-accent) 6%, transparent)',
        } as CSSProperties
      }
    >
      <Avatar className={styles.avatar}>
        <Icon aria-hidden="true" />
      </Avatar>
      <div className={styles.text}>
        {showName ? (
          <Typography variant="subtitle2" className={styles.name}>
            {name}
          </Typography>
        ) : null}
        <Typography variant="caption" component="p" className={styles.roleLine} id={`${composerId}-role-description`}>
          {roleLine}
        </Typography>
        {hasNotes ? (
          personalize.canPersonalize ? (
            <Link component="button" type="button" className={styles.notes} onClick={personalize.openDialog}>
              {personalizeCopy.notes}
            </Link>
          ) : (
            <Typography variant="caption" component="p" className={styles.notes}>
              {personalizeCopy.notes}
            </Typography>
          )
        ) : null}
      </div>
      {/* Disabled, not hidden, while the agent answers: the header doesn't jump and focus isn't lost (Joni, #2523) */}
      {newConversation.isOffered ? (
        <Button
          size="small"
          variant="text"
          className={styles.personalize}
          aria-label={`New conversation with ${name}`}
          disabled={newConversation.isDisabled}
          onClick={() => void newConversation.start()}
        >
          New conversation
        </Button>
      ) : null}
      {personalize.canPersonalize ? (
        <Button
          size="small"
          variant="text"
          className={styles.personalize}
          aria-label={personalizeCopy.entryName(name)}
          onClick={personalize.openDialog}
        >
          {personalizeCopy.entry}
        </Button>
      ) : null}
      {personalize.dialog}
      {personalize.toast}
    </div>
  )
}
